import React, { useState, useEffect } from 'react';
import type { CSSProperties } from 'react';
import { TravelChat } from './TravelChat';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Chat 模块入口
 * 
 * 支持通过 URL 参数直接打开聊天
 * 例如：#chat?groupId=xxx&groupName=yyy
 */
export const ChatModule: React.FC = () => {
  // 示例群组列表
  const [selectedGroup, setSelectedGroup] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // 从 router 的 query / legacy hash 读取群组信息，兼容两者
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // 首先尝试从 querystring 中读取（/chat?groupId=...&groupName=...）
    const searchParams = new URLSearchParams(location.search || '');
    let groupId = searchParams.get('groupId');
    let groupName = searchParams.get('groupName');

    // 回退：仍支持 #chat?groupId=... 的旧写法
    if (!groupId && !groupName) {
      const hash = window.location.hash || '';
      const hashQuery = hash.includes('?') ? hash.split('?')[1] : '';
      const params = new URLSearchParams(hashQuery);
      groupId = params.get('groupId') || null;
      groupName = params.get('groupName') || null;
    }

    if (groupId && groupName) {
      try {
        setSelectedGroup({ id: groupId, name: decodeURIComponent(groupName) });
      } catch (e) {
        setSelectedGroup({ id: groupId, name: groupName as string });
      }
    }
  }, [location]);

  // 示例群组数据（实际应该从后端获取）
  const groups = [
    { id: '2629bfe4-7f35-43f4-a4cf-de12a6b2a4a4', name: '东京白色圣诞' },
    // 可以添加更多群组
  ];

  const handleBack = () => {
    // Clear selected group state first so UI shows group list if navigation
    // doesn't actually change the location (fallbacks).
    setSelectedGroup(null);

    // Prefer going back in history so the user returns to the previous page
    // (e.g. the TravelDetail they came from). If history back isn't
    // available, fall back to a safe route or legacy hash.
    try {
      // navigate(-1) uses react-router history to go back one entry
      navigate(-1);
      return;
    } catch (e) {
      // ignore and try other fallbacks
    }

    if (typeof window !== 'undefined' && window.history && typeof window.history.back === 'function') {
      window.history.back();
      return;
    }

    // Last resort: navigate to the chat list route (legacy behavior)
    try {
      navigate('/chat');
    } catch (e) {
      if (typeof window !== 'undefined') window.location.hash = '#chat';
    }
  };

  if (selectedGroup) {
    return (
      <div style={styles.safeArea}>
        <TravelChat
          groupId={selectedGroup.id}
          groupName={selectedGroup.name}
          onBack={handleBack}
        />
      </div>
    );
  }

  // 群组列表
  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>我的群组</h1>
      </div>
      <div style={styles.list}>
        {groups.map((group) => (
          <button
            key={group.id}
            onClick={() => setSelectedGroup(group)}
            style={styles.groupItem}
          >
            <div style={styles.groupIcon}>💬</div>
            <div style={styles.groupInfo}>
              <h3 style={styles.groupName}>{group.name}</h3>
              <p style={styles.groupId}>ID: {group.id.slice(0, 8)}...</p>
            </div>
            <div style={styles.arrow}>→</div>
          </button>
        ))}
      </div>
    </div>
  );
};

const styles: Record<string, CSSProperties> = {
  safeArea: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: '#F3F4F6',
    padding: 0,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
  },
  header: {
    backgroundColor: '#fff',
    borderBottom: '1px solid #e0e0e0',
    padding: '12px 16px',
  },
  title: {
    fontSize: 20,
    fontWeight: 600,
    margin: 0,
    color: '#333',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    padding: 16,
    gap: 12,
  },
  groupItem: {
    backgroundColor: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    transition: 'box-shadow 0.2s',
  },
  groupIcon: {
    fontSize: 32,
  },
  groupInfo: {
    flex: 1,
  },
  groupName: {
    fontSize: 16,
    fontWeight: 500,
    margin: 0,
    color: '#333',
  },
  groupId: {
    fontSize: 12,
    color: '#999',
    margin: 0,
  },
  arrow: {
    fontSize: 20,
    color: '#999',
  },
};
